"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { getbook } from "./api"
import { Viewer, SpecialZoomLevel, Worker } from "@react-pdf-viewer/core"
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout"
import { selectionModePlugin } from "@react-pdf-viewer/selection-mode"
import "@react-pdf-viewer/core/lib/styles/index.css"
import "@react-pdf-viewer/default-layout/lib/styles/index.css"
import {
  ArrowLeft,
  ArrowRight,
  MessageCircle,
  Sparkles,
  Copy,
  Check,
  ImageIcon,
  Search,
  AlertCircle,
  BookOpen,
  HelpCircle,
  Brain,
  Crop,
  GitBranch,
  GitMerge,
  Loader2,
  X,
  Camera,
  Plus,
} from "lucide-react"
import { generatePresignedUrl, postScreenshotTeacherLibrary } from "../postFileAws"
import html2canvas from "html2canvas"
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf"
import Flashcard from "./FlashCard"
import QuizQuestion from "./QuizQuestion"
import MnemonicDevice from "./MnemonicDevice"
import FlowChart from "./FlowChart"
import MindMap from "./MindMap"
import ReactMarkdown from "react-markdown"
import { BlockMath, InlineMath } from "react-katex"
import "katex/dist/katex.min.css"
import { postAws } from "../postAws"

// Configure PDF.js worker for Vite
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`

const StudyBuddy = () => {
  const [book, setBook] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isChatOpen, setIsChatOpen] = useState(true)
  const [chatMessage, setChatMessage] = useState("")
  const [chatMessages, setChatMessages] = useState([])
  const [scale, setScale] = useState(SpecialZoomLevel.PageFit)
  const [selectedText, setSelectedText] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [askAIPosition, setAskAIPosition] = useState({ x: 0, y: 0 })
  const [showAskAIButton, setShowAskAIButton] = useState(false)
  const [chatWidth, setChatWidth] = useState(window.innerWidth * 0.4)
  const [isResizing, setIsResizing] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [startPos, setStartPos] = useState({ x: 0, y: 0 })
  const [highlightStyle, setHighlightStyle] = useState({})
  const [screenshot, setScreenshot] = useState(null)
  const [screenshotUrl, setScreenshotUrl] = useState(null)
  const [isScreenshotMode, setIsScreenshotMode] = useState(false)
  const [fullScreenImage, setFullScreenImage] = useState(null)
  const [wsConnection, setWsConnection] = useState(null)
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false)
  const [chatError, setChatError] = useState(null)
  const [selectedAction, setSelectedAction] = useState("understand")
  const [screenshotPreview, setScreenshotPreview] = useState(null)
  const [copiedMessageId, setCopiedMessageId] = useState(null)
  const [startPage, setStartPage] = useState(1)
  const [endPage, setEndPage] = useState(30)
  const [isWebSocketConnecting, setIsWebSocketConnecting] = useState(false)
  const [wsStatus, setWsStatus] = useState("disconnected")
  const [extractedText, setExtractedText] = useState(null)
  const [isPdfProcessing, setIsPdfProcessing] = useState(false)
  const [showSendMessage, setShowSendMessage] = useState(false)
  const [screenshotPresignedUrls, setScreenshotPresignedUrls] = useState({})
  const [worker, setWorker] = useState(null)
  const [mode, setMode] = useState("chat")
  const [isHovered, setIsHovered] = useState(false)
  const [isSaved, setIsSaved] = useState(false)
  const [pageRange, setPageRange] = useState({ start: 1, end: 1 })
  const [screenshotOverlay, setScreenshotOverlay] = useState(false)
  const [isScreenshotLoading, setIsScreenshotLoading] = useState(false)
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false)
  const pdfContainerRef = useRef(null)
  const chatResizeRef = useRef(null)
  const chatContentRef = useRef(null)
  const workerRef = useRef(null)
  const { bookId } = useParams()
  const [attachment, setAttachment] = useState(null)
  const [attachmentPreview, setAttachmentPreview] = useState(null)
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false)
  const [attachmentPresignedUrls, setAttachmentPresignedUrls] = useState({})
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768)
  const [showPdfOnMobile, setShowPdfOnMobile] = useState(true)
  const [screenshotNotification, setScreenshotNotification] = useState(false)
  const [isTutorActive, setIsTutorActive] = useState(false)
  const [showAddToNotesModal, setShowAddToNotesModal] = useState(false)
  const [selectedMessageContent, setSelectedMessageContent] = useState("")
  const [newNoteName, setNewNoteName] = useState("")
  const [existingNotes, setExistingNotes] = useState([])
  const [selectedExistingNote, setSelectedExistingNote] = useState("")
  const [isCreatingNewNote, setIsCreatingNewNote] = useState(true)

  // Enhanced state variables for note folders functionality
  const [noteFolders, setNoteFolders] = useState([])
  const [selectedFolder, setSelectedFolder] = useState("")
  const [isLoadingFolders, setIsLoadingFolders] = useState(false)
  const [customNoteName, setCustomNoteName] = useState("") // New state for custom note name

  const defaultLayoutPluginInstance = defaultLayoutPlugin()
  const selectionModePluginInstance = selectionModePlugin()

  const [dataChannel, setDataChannel] = useState(null)
  const peerConnection = useRef(null)
  const audioElement = useRef(null)
  const navigate = useNavigate()

  // Enhanced function to preprocess streaming content for better real-time rendering
  const preprocessStreamingContent = useCallback((content) => {
    if (!content) return ""

    let processedContent = content

    // Handle escaped newlines
    processedContent = processedContent.replace(/\\n/g, "\n")

    // Handle response JSON format if present - IMPROVED LOGIC
    if (processedContent.includes('{"response":')) {
      try {
        // Try to extract just the response content from partial JSON
        const responseMatch = processedContent.match(/\{"response":"(.*?)(?:"(?:,.*)?}?)?$/s)
        if (responseMatch) {
          processedContent = responseMatch[1]
          // Unescape the content
          processedContent = processedContent.replace(/\\"/g, '"')
          processedContent = processedContent.replace(/\\\\/g, "\\")
        } else {
          // Fallback: try to parse complete JSON
          const jsonObj = JSON.parse(processedContent)
          if (jsonObj.response) {
            processedContent = jsonObj.response
          }
        }
      } catch (e) {
        // If parsing fails, try to extract content manually
        const startIndex = processedContent.indexOf('{"response":"') + 13
        if (startIndex > 12) {
          processedContent = processedContent.substring(startIndex)
          // Remove trailing quotes and braces if present
          processedContent = processedContent.replace(/["}\]]*$/, "")
          // Unescape content
          processedContent = processedContent.replace(/\\"/g, '"')
          processedContent = processedContent.replace(/\\\\/g, "\\")
        }
      }
    }

    // Clean up any remaining formatting artifacts
    processedContent = processedContent.replace(/\\"/g, '"')
    processedContent = processedContent.replace(/\\\\/g, "\\")

    // Remove follow-up questions from the main content
    processedContent = processedContent.replace(/Follow-up Questions?:[\s\S]*$/i, "")
    processedContent = processedContent.replace(/\*\*Follow-up Questions?\*\*:[\s\S]*$/i, "")

    // Remove any trailing whitespace
    processedContent = processedContent.trim()

    return processedContent
  }, [])

  // Enhanced function to finalize the streaming message
  const finalizeStreamingMessage = useCallback(
    (messageType, state) => {
      let parsedContent = null
      let followUpQuestions = []
      let finalContent = state.content

      try {
        parsedContent = JSON.parse(state.content)
        console.log("Parsed chat content as JSON:", parsedContent)

        if (parsedContent.response) {
          finalContent = parsedContent.response
        }

        if (parsedContent.followUpQuestions) {
          followUpQuestions = parsedContent.followUpQuestions
        } else if (parsedContent.response) {
          const match = parsedContent.response.match(/Follow-up Questions?:([\s\S]*?)(?:\n\n|$)/i)
          if (match) {
            followUpQuestions = match[1]
              .split("\n")
              .filter((q) => q.trim())
              .map((q) =>
                q
                  .replace(/^\d+\.\s*/, "")
                  .replace(/^[*-]\s*/, "")
                  .trim(),
              )
              .filter((q) => q.length > 0)
          }
        }
      } catch (e) {
        console.log("Content is not valid JSON, using as plain text:", state.content)
        finalContent = preprocessStreamingContent(state.content)

        // Try to extract follow-up questions from plain text
        const match = finalContent.match(/Follow-up Questions?:([\s\S]*?)(?:\n\n|$)/i)
        if (match) {
          followUpQuestions = match[1]
            .split("\n")
            .filter((q) => q.trim())
            .map((q) =>
              q
                .replace(/^\d+\.\s*/, "")
                .replace(/^[*-]\s*/, "")
                .trim(),
            )
            .filter((q) => q.length > 0)

          // Remove follow-up questions from the final content
          finalContent = finalContent.replace(/Follow-up Questions?:[\s\S]*$/i, "").trim()
        }
      }

      const completeMessage = {
        role: "assistant",
        content: finalContent,
        followUpQuestions: followUpQuestions,
        isStreaming: false,
      }

      console.log("Final chat message:", completeMessage)

      setChatMessages((prevMessages) => {
        return prevMessages.map((msg) => {
          if (msg.streamingId === state.messageId) {
            return completeMessage
          }
          return msg
        })
      })

      window.streamingState[messageType] = null
      setIsWaitingForResponse(false)
    },
    [preprocessStreamingContent],
  )

  // Enhanced streaming chat processor with real-time rendering
  const processStreamingChat = useCallback(
    (messageType) => {
      const state = window.streamingState[messageType]

      state.chunks.sort((a, b) => a.index - b.index)

      let updatedContent = ""
      let isComplete = false

      for (const chunk of state.chunks) {
        updatedContent += chunk.content || ""

        if (chunk.isFinished) {
          isComplete = true
          state.isComplete = true
        }
      }

      const cleanedContent = preprocessStreamingContent(updatedContent)
      state.content = updatedContent

      setChatMessages((prevMessages) => {
        return prevMessages.map((msg) => {
          if (msg.isStreaming && msg.streamingId === state.messageId) {
            return {
              ...msg,
              content: cleanedContent,
              isStreaming: !isComplete,
            }
          }
          return msg
        })
      })

      if (isComplete) {
        finalizeStreamingMessage(messageType, state)
      }
    },
    [preprocessStreamingContent, finalizeStreamingMessage],
  )

  // Enhanced function to handle plain text streaming with better formatting
  const processPlainTextStreaming = useCallback(
    (textContent) => {
      if (!window.streamingState) {
        window.streamingState = {}
      }

      if (!window.streamingState.plainText) {
        window.streamingState.plainText = {
          content: "",
          messageId: Date.now() + Math.random(),
        }

        setChatMessages((prevMessages) => [
          ...prevMessages,
          {
            role: "assistant",
            content: "",
            isStreaming: true,
            streamingId: window.streamingState.plainText.messageId,
          },
        ])
      }

      window.streamingState.plainText.content += textContent
      const cleanedContent = preprocessStreamingContent(window.streamingState.plainText.content)

      setChatMessages((prevMessages) => {
        return prevMessages.map((msg) => {
          if (msg.isStreaming && msg.streamingId === window.streamingState.plainText.messageId) {
            return {
              ...msg,
              content: cleanedContent,
            }
          }
          return msg
        })
      })
    },
    [preprocessStreamingContent],
  )

  // FIXED: Process non-streaming types (flashcard, quiz, mnemonic, flowchart)
  // Only show loading indicator, no partial content
  const processNonStreamingType = (messageType) => {
    const state = window.streamingState[messageType]

    state.chunks.sort((a, b) => a.index - b.index)

    let fullContent = ""
    let isComplete = false

    for (const chunk of state.chunks) {
      fullContent += chunk.content || ""
      if (chunk.isFinished) {
        isComplete = true
      }
    }

    // FIXED: For non-chat types, don't update anything during streaming
    // Only show thinking dots until complete
    if (!isComplete) {
      // Keep showing thinking dots - don't update message content
      return
    }

    // Rest of the function remains the same for when complete...
    console.log(`Combined ${messageType} content:`, fullContent)

    let parsedContent = null
    try {
      if (messageType === "quiz") {
        console.log(`Attempting to parse quiz data: ${fullContent}`)
        const match = fullContent.match(/\{"questionArray":\[(.*)\]\}/)
        if (match) {
          try {
            parsedContent = { questionArray: JSON.parse(`[${match[1]}]`) }
            console.log("Successfully parsed quiz data:", parsedContent)
          } catch (e) {
            console.error("Error parsing extracted quiz data:", e)
          }
        }

        if (!parsedContent) {
          let cleanedContent = fullContent
          if (fullContent.endsWith("]}")) {
            const lastBracketIndex = fullContent.lastIndexOf("}]}")
            if (lastBracketIndex > 0) {
              cleanedContent = fullContent.substring(0, lastBracketIndex + 2)
            }
          }

          try {
            parsedContent = JSON.parse(cleanedContent)
            console.log("Successfully parsed cleaned quiz data:", parsedContent)
          } catch (e) {
            console.error("Error parsing cleaned quiz data:", e)
          }
        }
      } else {
        parsedContent = JSON.parse(fullContent)
        console.log(`Parsed ${messageType} content as JSON:`, parsedContent)
      }
    } catch (e) {
      console.log(`${messageType} content is not valid JSON, using as plain text:`, e)
    }

    const newMessage = {
      role: "assistant",
      content: "",
      followUpQuestions: [],
    }

    if (parsedContent && parsedContent.followUpQuestions) {
      newMessage.followUpQuestions = parsedContent.followUpQuestions
    } else if (parsedContent && parsedContent.response) {
      const match = parsedContent.response.match(/Follow-up Questions:([\s\S]*?)(?:\n\n|$)/)
      if (match) {
        newMessage.followUpQuestions = match[1]
          .split("\n")
          .filter((q) => q.trim())
          .map((q) => q.replace(/^\d+\.\s*/, "").trim())
      }
    }

    let hasSpecializedContent = false

    if (messageType === "flashcard" && parsedContent) {
      if (parsedContent.front && parsedContent.back) {
        newMessage.flashcard = {
          front: parsedContent.front,
          back: parsedContent.back,
        }
        hasSpecializedContent = true
      }
    } else if (messageType === "quiz" && parsedContent) {
      if (parsedContent.questionArray) {
        newMessage.questionArray = parsedContent.questionArray
        hasSpecializedContent = true
      }
    } else if (messageType === "mnemonic" && parsedContent) {
      if (parsedContent.shortForm && parsedContent.explanation) {
        newMessage.mnemonicDevice = {
          shortForm: parsedContent.shortForm,
          explanation: parsedContent.explanation,
        }
        hasSpecializedContent = true
      }
    } else if (messageType === "mnemonic" && fullContent) {
      // Try to parse the content directly if parsedContent failed
      try {
        const directParsed = JSON.parse(fullContent)
        if (directParsed.shortForm && directParsed.explanation) {
          newMessage.mnemonicDevice = {
            shortForm: directParsed.shortForm,
            explanation: directParsed.explanation,
          }
          hasSpecializedContent = true
        }
      } catch (e) {
        console.log("Failed to parse mnemonic content directly:", e)
      }
    } else if (messageType === "flowchart" && parsedContent) {
      if (parsedContent.flowchart) {
        newMessage.flowchart = parsedContent.flowchart
        hasSpecializedContent = true
      }
    } else if (messageType === "mindmap" && parsedContent) {
      if (parsedContent.mindmap) {
        newMessage.mindmap = parsedContent.mindmap
        hasSpecializedContent = true
      }
    }

    if (!hasSpecializedContent) {
      newMessage.content = parsedContent ? parsedContent.response || fullContent : fullContent
    }

    console.log(`Final ${messageType} message:`, newMessage)

    // Replace the streaming message with the final component
    setChatMessages((prevMessages) => {
      return prevMessages.map((msg) => {
        if (msg.isStreaming && msg.streamingId === state.messageId) {
          return newMessage
        }
        return msg
      })
    })

    window.streamingState[messageType] = null
    setIsWaitingForResponse(false)
  }

  // Helper function to handle special message types and new structured responses
  const handleSpecialMessageTypes = useCallback((message) => {
    // Handle new structured messages from backend
    if (message.type) {
      console.log("Processing structured message:", message)

      switch (message.type) {
        case "streaming":
          handleStreamingMessage(message)
          return true
        case "complete":
          handleCompleteMessage(message)
          return true
        case "error":
          handleErrorMessage(message)
          return true
        default:
          console.warn("Unknown structured message type:", message.type)
          return false
      }
    }

    // Legacy message handling
    if (typeof message.message !== "string") return false

    // Check for mindmap
    if (message.message.includes('"mindmap"')) {
      try {
        const parsedOuter = JSON.parse(message.message)
        if (parsedOuter.mindmap) {
          console.log("Received complete mindmap data:", parsedOuter)
          setChatMessages((prevMessages) => [
            ...prevMessages,
            {
              role: "assistant",
              content: "",
              mindmap: parsedOuter.mindmap,
              followUpQuestions: [],
            },
          ])
          setIsWaitingForResponse(false)
          return true
        }
      } catch (e) {
        console.log("Error parsing mindmap data:", e)
      }
    }

    // Check for quiz
    if (message.message.includes('"questionArray"')) {
      try {
        const parsedOuter = JSON.parse(message.message)
        if (parsedOuter.questionArray) {
          console.log("Received complete quiz data:", parsedOuter)
          setChatMessages((prevMessages) => [
            ...prevMessages,
            {
              role: "assistant",
              content: "",
              questionArray: parsedOuter.questionArray,
              followUpQuestions: [],
            },
          ])
          setIsWaitingForResponse(false)
          return true
        }
      } catch (e) {
        console.log("Error parsing quiz data:", e)
      }
    }

    // Check for mnemonic
    if (message.message.includes('"shortForm"') && message.message.includes('"explanation"')) {
      try {
        const parsedOuter = JSON.parse(message.message)
        if (parsedOuter.shortForm && parsedOuter.explanation) {
          console.log("Received complete mnemonic data:", parsedOuter)
          setChatMessages((prevMessages) => [
            ...prevMessages,
            {
              role: "assistant",
              content: "",
              mnemonicDevice: {
                shortForm: parsedOuter.shortForm,
                explanation: parsedOuter.explanation,
              },
              followUpQuestions: [],
            },
          ])
          setIsWaitingForResponse(false)
          return true
        }
      } catch (e) {
        console.log("Error parsing mnemonic data:", e)
      }
    }

    // Also check if content field contains mnemonic data
    if (message.content && typeof message.content === "string") {
      try {
        const contentData = JSON.parse(message.content)
        if (contentData.shortForm && contentData.explanation) {
          console.log("Received mnemonic data in content field:", contentData)
          setChatMessages((prevMessages) => [
            ...prevMessages,
            {
              role: "assistant",
              content: "",
              mnemonicDevice: {
                shortForm: contentData.shortForm,
                explanation: contentData.explanation,
              },
              followUpQuestions: message.followUpQuestions || [],
            },
          ])
          setIsWaitingForResponse(false)
          return true
        }
      } catch (e) {
        // Not JSON or doesn't contain mnemonic data, continue with normal processing
      }
    }

    // Check for flashcard
    if (message.message.includes('"front"') && message.message.includes('"back"')) {
      try {
        const parsedOuter = JSON.parse(message.message)
        if (parsedOuter.front && parsedOuter.back) {
          console.log("Received complete flashcard data:", parsedOuter)
          setChatMessages((prevMessages) => [
            ...prevMessages,
            {
              role: "assistant",
              content: "",
              flashcard: {
                front: parsedOuter.front,
                back: parsedOuter.back,
              },
              followUpQuestions: [],
            },
          ])
          setIsWaitingForResponse(false)
          return true
        }
      } catch (e) {
        console.log("Error parsing flashcard data:", e)
      }
    }

    // Check for flowchart
    if (message.message.includes('"flowchart"')) {
      try {
        const parsedOuter = JSON.parse(message.message)
        if (parsedOuter.flowchart) {
          console.log("Received complete flowchart data:", parsedOuter)
          setChatMessages((prevMessages) => [
            ...prevMessages,
            {
              role: "assistant",
              content: "",
              flowchart: parsedOuter.flowchart,
              followUpQuestions: [],
            },
          ])
          setIsWaitingForResponse(false)
          return true
        }
      } catch (e) {
        console.log("Error parsing flowchart data:", e)
      }
    }

    return false
  }, [])

  // Handle streaming messages from new backend structure
  const handleStreamingMessage = useCallback(
    (message) => {
      const { messageType, index, content, accumulatedContent } = message

      // FIXED: For non-chat modes, don't show streaming content
      if (messageType !== "chat") {
        return
      }

      // Find or create streaming message
      setChatMessages((prevMessages) => {
        const existingIndex = prevMessages.findIndex((msg) => msg.isStreaming && msg.streamingType === messageType)

        if (existingIndex >= 0) {
          // Update existing streaming message
          const updatedMessages = [...prevMessages]
          updatedMessages[existingIndex] = {
            ...updatedMessages[existingIndex],
            content: preprocessStreamingContent(accumulatedContent || content),
            lastIndex: index,
          }
          return updatedMessages
        } else {
          // Create new streaming message
          return [
            ...prevMessages,
            {
              role: "assistant",
              content: preprocessStreamingContent(accumulatedContent || content),
              isStreaming: true,
              streamingType: messageType,
              lastIndex: index,
            },
          ]
        }
      })
    },
    [preprocessStreamingContent],
  )

  // Handle complete messages from new backend structure
  const handleCompleteMessage = useCallback((message) => {
    const { messageType } = message

    setChatMessages((prevMessages) => {
      // Remove any streaming message of the same type
      const filteredMessages = prevMessages.filter((msg) => !(msg.isStreaming && msg.streamingType === messageType))

      // Create final message based on type
      let finalMessage = {
        role: "assistant",
        isStreaming: false,
      }

      switch (messageType) {
        case "chat":
          finalMessage = {
            ...finalMessage,
            content: message.response,
            followUpQuestions: message.followUpQuestions || [],
          }
          break
        case "flashcard":
          finalMessage = {
            ...finalMessage,
            content: "",
            flashcard: {
              front: message.front,
              back: message.back,
            },
          }
          break
        case "quiz":
          finalMessage = {
            ...finalMessage,
            content: "",
            questionArray: message.questionArray,
          }
          break
        case "mnemonic":
          finalMessage = {
            ...finalMessage,
            content: "",
            mnemonicDevice: {
              shortForm: message.shortForm,
              explanation: message.explanation,
            },
          }
          break
        case "flowchart":
          finalMessage = {
            ...finalMessage,
            content: "",
            flowchart: message.flowchart,
          }
          break
        case "mindmap":
          finalMessage = {
            ...finalMessage,
            content: "",
            mindmap: message.mindmap,
          }
          break
        default:
          finalMessage = {
            ...finalMessage,
            content: message.response || "Response received",
          }
      }

      return [...filteredMessages, finalMessage]
    })

    setIsWaitingForResponse(false)
  }, [])

  // Handle error messages from new backend structure
  const handleErrorMessage = useCallback((message) => {
    setChatError(message.error)
    setIsWaitingForResponse(false)

    // Remove any streaming messages
    setChatMessages((prevMessages) => prevMessages.filter((msg) => !msg.isStreaming))
  }, [])

  // Modified useEffect to fetch both note folders and existing notes
  useEffect(() => {
    const fetchNotesData = async () => {
      if (!showAddToNotesModal) return

      try {
        setIsLoadingFolders(true)

        // Fetch note folders
        const foldersResponse = await postAws("https://api-dev.aurait.ai/notes/updateNotes", "POST", {
          request: "get-note-folders",
          userId: localStorage.getItem("teacherId"),
          classRoomId: localStorage.getItem("classRoomId"),
        })

        if (foldersResponse && foldersResponse.body) {
          const foldersData = JSON.parse(foldersResponse.body)
          setNoteFolders(foldersData.folders || [])
        }

        // Fetch existing notes (keep existing functionality)
        const notesResponse = await postAws("https://api-dev.aurait.ai/notes/list", "POST", {
          userId: localStorage.getItem("teacherId"),
          classRoomId: localStorage.getItem("classRoomId"),
        })

        if (notesResponse && notesResponse.body) {
          const notes = JSON.parse(notesResponse.body)
          setExistingNotes(notes.data || [])
        }
      } catch (error) {
        console.error("Error fetching notes data:", error)
      } finally {
        setIsLoadingFolders(false)
      }
    }

    if (showAddToNotesModal) {
      fetchNotesData()
    }
  }, [showAddToNotesModal])

  // Enhanced handleAddToNotes function
  const handleAddToNotes = (type, content) => {
    console.log("Adding to notes - Type:", type, "Content:", content)

    let noteContent

    // Handle different content types properly
    if (type === "mnemonic" && content && typeof content === "object") {
      noteContent = {
        noteType: "mnemonic",
        noteData: {
          shortForm: content.shortForm,
          explanation: content.explanation,
        },
      }
    } else if (type === "flashcard" && content && typeof content === "object") {
      noteContent = {
        noteType: "flashcard",
        noteData: {
          front: content.front,
          back: content.back,
        },
      }
    } else if (type === "flowchart" && content) {
      noteContent = {
        noteType: "flowchart",
        noteData: content,
      }
    } else if (type === "mindmap" && content) {
      noteContent = {
        noteType: "mindmap",
        noteData: content,
      }
    } else if (type === "quiz" && content) {
      noteContent = {
        noteType: "quiz",
        noteData: content,
      }
    } else {
      // Default to text type
      noteContent = {
        noteType: "text",
        noteData: content,
      }
    }

    console.log("Processed note content:", noteContent)

    setSelectedMessageContent(noteContent)
    setShowAddToNotesModal(true)
    setNewNoteName("")
    setSelectedFolder("")
    setCustomNoteName("") // Reset custom note name
    setIsCreatingNewNote(true)
  }

  // Enhanced function to generate default note name based on content type
  const generateDefaultNoteName = (noteContent) => {
    const timestamp = new Date().toLocaleString()
    const contentType = noteContent.noteType || "note"

    switch (contentType) {
      case "flashcard":
        return `Flashcard - ${timestamp}`
      case "mnemonic":
        return `Mnemonic - ${timestamp}`
      case "quiz":
        return `Quiz - ${timestamp}`
      case "flowchart":
        return `Flowchart - ${timestamp}`
      case "mindmap":
        return `Mind Map - ${timestamp}`
      case "text":
      default:
        return `Study Note - ${timestamp}`
    }
  }

  // Enhanced handleSaveToNotesModal function with improved note naming
  const handleSaveToNotesModal = async () => {
    if (isCreatingNewNote && !newNoteName.trim()) {
      alert("Please enter a name for your new note")
      return
    }

    if (!isCreatingNewNote && !selectedFolder) {
      alert("Please select a folder")
      return
    }

    console.log("Saving note with content:", selectedMessageContent)

    try {
      if (isCreatingNewNote) {
        // Create new note with proper structure
        const notesData = {
          classRoomId: localStorage.getItem("classRoomId"),
          noteTitle: newNoteName,
          refId: book._id,
          noteFolder: book.fileTitle,
          userId: localStorage.getItem("teacherId"),
          userType: "teacher",
          noteContents: [
            {
              aSide: {
                noteType: selectedMessageContent.noteType,
                noteData: selectedMessageContent.noteData,
              },
              bSide: {
                noteType: "NONE",
                noteData: null,
              },
            },
          ],
        }

        console.log("Creating new note with data:", notesData)
        const response = await postAws("https://api-dev.aurait.ai/notes", "POST", notesData)
        if (response.statusCode === 201) {
          setIsSaved(true)
          setTimeout(() => setIsSaved(false), 2000)
        }
      } else {
        // Add to existing folder with enhanced note naming
        const finalNoteName = customNoteName.trim() || generateDefaultNoteName(selectedMessageContent)

        const addToFolderData = {
          request: "add-note-to-folder",
          userId: localStorage.getItem("teacherId"),
          classRoomId: localStorage.getItem("classRoomId"),
          noteFolder: selectedFolder,
          noteTitle: finalNoteName,
          refId: book._id,
          userType: "teacher",
          initialContent: [
            {
              aSide: {
                noteType: selectedMessageContent.noteType,
                noteData: selectedMessageContent.noteData,
              },
              bSide: {
                noteType: "NONE",
                noteData: null,
              },
            },
          ],
        }

        console.log("Adding to folder with data:", addToFolderData)
        const response = await postAws("https://api-dev.aurait.ai/notes/updateNotes", "POST", addToFolderData)
        if (response && (response.statusCode === 200 || response.statusCode === 201)) {
          setIsSaved(true)
          setTimeout(() => setIsSaved(false), 2000)
        }
      }
    } catch (error) {
      console.error("Error saving note:", error)
      alert("Error saving note. Please try again.")
    }

    setShowAddToNotesModal(false)
  }

  async function startSession() {
    try {
      const tokenData = {
        request: "realtime-Token",
        s3uri: book.textUrl,
      }

      const tokenResponse = await postAws("https://api-dev.aurait.ai/pathyaguru", "POST", tokenData)

      if (tokenResponse.statusCode !== 200) {
        setChatError("Error connecting to human tutor. Please try again later.")
        setIsTutorActive(false)
        return false
      }

      const data = await tokenResponse.body
      const EPHEMERAL_KEY = data.client_secret.value

      const pc = new RTCPeerConnection()

      audioElement.current = document.createElement("audio")
      audioElement.current.autoplay = true
      pc.ontrack = (e) => (audioElement.current.srcObject = e.streams[0])

      const ms = await navigator.mediaDevices.getUserMedia({
        audio: true,
      })
      pc.addTrack(ms.getTracks()[0])

      const dc = pc.createDataChannel("oai-events")
      setDataChannel(dc)

      const offer = await pc.createOffer()
      await pc.setLocalDescription(offer)

      const baseUrl = "https://api.openai.com/v1/realtime"
      const model = "gpt-4o-realtime-preview-2024-12-17"
      const sdpResponse = await fetch(`${baseUrl}?model=${model}`, {
        method: "POST",
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${EPHEMERAL_KEY}`,
          "Content-Type": "application/sdp",
        },
      })

      const answer = {
        type: "answer",
        sdp: await sdpResponse.text(),
      }
      await pc.setRemoteDescription(answer)

      peerConnection.current = pc
      return true
    } catch (error) {
      console.error("Error starting tutor session:", error)
      setChatError("Error connecting to human tutor. Please try again later.")
      setIsTutorActive(false)
      return false
    }
  }

  function sendClientEvent(message) {
    if (dataChannel) {
      const timestamp = new Date().toLocaleTimeString()
      message.event_id = message.event_id || crypto.randomUUID()

      dataChannel.send(JSON.stringify(message))

      if (!message.timestamp) {
        message.timestamp = timestamp
      }
    } else {
      console.error("Failed to send message - no data channel available", message)
    }
  }

  function sendTextMessage(message) {
    const event = {
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "user",
        content: [
          {
            type: "input_text",
            text: message,
          },
        ],
      },
    }

    sendClientEvent(event)
    sendClientEvent({ type: "response.create" })
  }

  function stopSession() {
    if (dataChannel) {
      dataChannel.close()
    }

    peerConnection.current.getSenders().forEach((sender) => {
      if (sender.track) {
        sender.track.stop()
      }
    })

    if (peerConnection.current) {
      peerConnection.current.close()
    }

    setDataChannel(null)
    peerConnection.current = null
  }

  async function pdfTextContext() {
    const presignedUrl = await generatePresignedUrl(book.textUrl)
    try {
      const response = await fetch(presignedUrl)
      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`)
      }

      const text = await response.text()

      const numberOfChunks = 5
      const chunkSize = Math.ceil(text.length / numberOfChunks)

      for (let i = 0; i < numberOfChunks; i++) {
        const chunk = text.slice(i * chunkSize, (i + 1) * chunkSize).trim()

        if (chunk.length > 0) {
          sendTextMessage(chunk)
        }
      }
    } catch (error) {
      console.error("Error fetching text from presigned URL:", error)
      throw error
    }
  }

  useEffect(() => {
    if (dataChannel) {
      dataChannel.addEventListener("message", (e) => {
        const event = JSON.parse(e.data)
        if (!event.timestamp) {
          event.timestamp = new Date().toLocaleTimeString()
        }
        console.log(event)
      })

      dataChannel.addEventListener("open", () => {
        pdfTextContext()
        sendTextMessage("Final text chunk Received, Start answering Query")
      })
    }
  }, [dataChannel])

  const actionPrompts = {
    understand: [""],
    analyze: [""],
    generate: [""],
  }

  const samplePrompts = {
    understand: [
      "Summarize the main points of this section",
      "Explain the concept of book in simple terms",
      "What are the key takeaways from doc?",
    ],
    analyze: [
      "Compare and contrast the ideas presented here",
      "What evidence supports the author's argument about doc?",
      "Identify potential weaknesses in the reasoning on doc",
    ],
    generate: [
      "Create an assignment on key concepts of this doc",
      "Develop a set of review questions for this section",
      "Generate a list of real-world applications for the concepts in this chapter",
    ],
  }

useEffect(() => {
  const fetchBook = async () => {
    try {
      if (!book) {
        setIsLoading(true)

        const bookData = await getbook(bookId)
        let s3Uri = bookData.data.fileUri

        console.log("Original fileUrl:", s3Uri)

        // Clean up any incorrect "eacher/" in the path
        if (s3Uri.includes("/eacher/")) {
          console.warn("Correcting fileUrl from 'eacher/' to 'teacher/'...")
          s3Uri = s3Uri.replace("/eacher/", "/Teachers/")
        }

        console.log("Corrected S3 URI for presigned URL:", s3Uri)

        let presignedUrl = await generatePresignedUrl(s3Uri)

        // Safety check: if presigned URL still has "/eacher/", fix it again
        if (presignedUrl.includes("/eacher/")) {
          console.warn("Presigned URL still contains '/eacher/', fixing...")
          presignedUrl = presignedUrl.replace("/eacher/", "/teacher/")
        }

        console.log("Final presigned URL:", presignedUrl)

        setBook({ ...bookData.data, fileUrl: s3Uri, presignedUrl })
      }
    } catch (error) {
      console.error("Error fetching book:", error)
      setError("Failed to load book. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  fetchBook()
}, [bookId, book])


  const extractPdfText = useCallback(async (uri) => {
    setIsPdfProcessing(true)
    try {
      const checkExtractionData = {
        url: uri,
        region: "ap-south-1",
        checkOnly: true,
      }

      const checkResponse = await postAws("https://api-dev.aurait.ai/library/extTxtPdf", "POST", checkExtractionData)

      if (checkResponse && checkResponse.body) {
        const parsedCheckBody = JSON.parse(checkResponse.body)
        if (parsedCheckBody.file_uri) {
          console.log("Extracted text URI is:", parsedCheckBody.file_uri)
        }
        if (parsedCheckBody.exists) {
          setExtractedText(parsedCheckBody.extractedText)
          setIsPdfProcessing(false)
          setShowSendMessage(true)
          return { extractedText: parsedCheckBody.extractedText, extracted: false }
        }
      }

      const extractionData = {
        url: uri,
        region: "ap-south-1",
      }

      const response = await postAws("https://api-dev.aurait.ai/library/extTxtPdf", "POST", extractionData)

      if (response && response.statusCode === 200 && response.body) {
        const parsedBody = JSON.parse(response.body)
        const extractedText = parsedBody.extractedText
        setExtractedText(extractedText)
        setIsPdfProcessing(false)
        setShowSendMessage(true)
        return { extractedText, extracted: true }
      } else {
        console.error("Invalid response format or extraction failed:", response)
        setIsPdfProcessing(false)
        setError("Failed to extract text from PDF. Please try again.")
        return { extractedText: null, extracted: false }
      }
    } catch (error) {
      console.error("Error extracting PDF text:", error)
      setIsPdfProcessing(false)
      setError("An error occurred while processing the PDF. Please try again.")
      return { extractedText: null, extracted: false }
    }
  }, [])

  const connectWebSocket = useCallback(() => {
    setChatError()

    setIsWebSocketConnecting(true)
    setWsStatus("connecting")
    console.log("Initiating new WebSocket connection")

    const ws = new WebSocket("wss://4nyy35mvj2.execute-api.ap-south-1.amazonaws.com/development/")

    ws.onopen = () => {
      console.log("WebSocket connected successfully")
      setWsConnection(ws)
      setIsWebSocketConnecting(false)
      setWsStatus("connected")
      ws.send(
        JSON.stringify({
          action: "setId",
          userId: localStorage.getItem("teacherId"),
          s3uri: book.textUrl,
        }),
      )
    }

    ws.onmessage = (event) => {
      if (typeof event.data === "string" && event.data.includes("Endpoint request timed out")) {
        console.log("Ignoring timeout message:", event.data)
        return
      }

      try {
        const message = JSON.parse(event.data)
        console.log("Received message:", message)

        if (message.message === "connection established with pdf") {
          setShowSendMessage(true)
          return
        }

        if (!message.message && !message.type) {
          setChatError("Please retry, there's some error.")
          return
        }

        // Handle special message types first (including new structured messages)
        if (handleSpecialMessageTypes(message)) {
          return
        }

        let parsedData

        try {
          parsedData = JSON.parse(message.message)
        } catch (error) {
          console.log("Error parsing message as JSON, treating as plain text:", error)
          processPlainTextStreaming(message.message)
          return
        }

        const messageType = parsedData.type || "chat"

        if (!window.streamingState) {
          window.streamingState = {}
        }

        if (!window.streamingState[messageType]) {
          window.streamingState[messageType] = {
            chunks: [],
            content: "",
            isComplete: false,
            lastProcessedIndex: -1,
            messageId: Date.now() + Math.random(),
          }

          // FIXED: For non-chat types, show ONLY thinking dots (same as chat waiting indicator)
          if (messageType !== "chat") {
            setChatMessages((prevMessages) => [
              ...prevMessages,
              {
                role: "assistant",
                content: "", // Empty content - only show thinking dots
                isStreaming: true,
                streamingId: window.streamingState[messageType].messageId,
                streamingType: messageType,
                showThinkingDots: true, // Flag to show thinking dots
              },
            ])
          } else {
            setChatMessages((prevMessages) => [
              ...prevMessages,
              {
                role: "assistant",
                content: "",
                isStreaming: true,
                streamingId: window.streamingState[messageType].messageId,
              },
            ])
          }
        }

        window.streamingState[messageType].chunks.push({
          index: parsedData.index,
          content: parsedData.response.delta.content || "",
          isFinished: parsedData.response.finish_reason === "stop",
          data: parsedData,
        })

        if (messageType === "chat") {
          processStreamingChat(messageType)
        } else {
          if (parsedData?.response?.finish_reason === "stop") {
            processNonStreamingType(messageType)
          }
        }
      } catch (error) {
        console.error("WebSocket message processing error:", error)
        setChatError("Please retry, there's some error.")
        setIsWaitingForResponse(false)
      }
    }

    ws.onclose = () => {
      console.log("WebSocket disconnected")
      setWsConnection(null)
      setIsWebSocketConnecting(false)
      setWsStatus("disconnected")
      setChatError("Study Buddy disconnected. Click to refresh.")
    }

    ws.onerror = (error) => {
      console.error("WebSocket error:", error)
      setIsWebSocketConnecting(false)
      setWsStatus("error")
      setChatError("Failed to connect to AI assistant. Please try again.")
    }
  }, [
    book,
    wsConnection,
    isWebSocketConnecting,
    handleSpecialMessageTypes,
    processStreamingChat,
    processPlainTextStreaming,
  ])

  useEffect(() => {
    if (book) {
      connectWebSocket()
      extractPdfText(book.fileUri)
    }

    return () => {
      if (wsConnection) {
        console.log("Cleaning up WebSocket connection")
        wsConnection.close()
        setWsConnection(null)
      }
    }
  }, [book, extractPdfText])

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768
      setIsMobileView(mobile)

      if (mobile) {
        setChatWidth(window.innerWidth)
      } else {
        setChatWidth(window.innerWidth * 0.4)
      }
    }

    const style = document.createElement("style")
    style.textContent = `
      @keyframes soundWave {
        0% { height: 3px; }
        50% { height: 12px; }
        100% { height: 3px; }
      }
      .animate-sound-wave {
        animation: soundWave 0.8s ease-in-out infinite;
      }
    `
    document.head.appendChild(style)

    window.addEventListener("resize", handleResize)
    handleResize()

    return () => {
      window.removeEventListener("resize", handleResize)
      document.head.removeChild(style)
    }
  }, [])

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isResizing) {
        const newWidth = window.innerWidth - e.clientX
        // Limit the minimum and maximum width
        const limitedWidth = Math.max(300, Math.min(window.innerWidth * 0.7, newWidth))
        setChatWidth(limitedWidth)
      }
    }

    const handleMouseUp = () => {
      setIsResizing(false)
    }

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseup", handleMouseUp)
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
    }
  }, [isResizing])

  const handleSendMessage = useCallback(
    async (e) => {
      e.preventDefault()
      if (!chatMessage.trim() && !screenshot && !attachment) return
      if (wsStatus !== "connected") {
        setChatError("Study Buddy is disconnected. Click to refresh.")
        return
      }

      const promptPrefix = actionPrompts[selectedAction][0]
      const fullMessage = `${chatMessage}`

      const newMessage = {
        role: "user",
        content: fullMessage,
        attachment: attachment || null,
      }

      let screenshotS3Url = null

      if (screenshot) {
        try {
          const res = await fetch(screenshot)
          const blob = await res.blob()

          const result = await postScreenshotTeacherLibrary(
            blob,
            localStorage.getItem("teacherId") || "",
            book.classRoomId,
            `screenshot_${Date.now()}.png`,
          )

          screenshotS3Url = result.url
          setScreenshotUrl(screenshotS3Url)
          newMessage.attachment = screenshotS3Url

          const presignedUrl = await generatePresignedUrl(screenshotS3Url)
          setScreenshotPresignedUrls((prev) => ({ ...prev, [screenshotS3Url]: presignedUrl }))
        } catch (error) {
          console.error("Error uploading screenshot:", error)
        } finally {
          setIsScreenshotLoading(false)
        }
      }

      setChatMessages((prevMessages) => [
        ...prevMessages.map((msg) => ({ ...msg, followUpQuestions: undefined })),
        newMessage,
      ])
      setIsWaitingForResponse(true)

      // FIXED: Add thinking dots immediately for non-chat modes
      if (mode !== "chat") {
        setChatMessages((prevMessages) => [
          ...prevMessages,
          {
            role: "assistant",
            content: "",
            isStreaming: true,
            streamingId: Date.now() + Math.random(),
            streamingType: mode,
            showThinkingDots: true,
          },
        ])
      }

      if (wsConnection) {
        const messageToSend = {
          action:
            mode === "flashcard"
              ? "createFlashcard"
              : mode === "quiz"
                ? "createQuiz"
                : mode === "mnemonic"
                  ? "createMnemonicDevice"
                  : mode === "mindmap"
                    ? "createMindMap"
                    : mode === "flowchart"
                      ? "createFlowChart"
                      : "sendMessage",
          message: fullMessage,
          refType: screenshot || attachment ? "image" : "text",
          s3url: attachment || screenshotS3Url || "",
        }

        console.log("Sending WebSocket message:", messageToSend)
        wsConnection.send(JSON.stringify(messageToSend))
      }

      setChatMessage("")
      setScreenshot(null)
      setScreenshotPreview(null)
      setAttachment(null)
      setAttachmentPreview(null)
    },
    [chatMessage, screenshot, wsConnection, book, mode, wsStatus, selectedAction, attachment],
  )

  // Fixed text selection handler to prevent over-selection
  const handleTextSelection = useCallback(() => {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      setShowAskAIButton(false)
      return
    }

    const selectedText = selection.toString().trim()

    setSelectedText("")
    setShowAskAIButton(false)

    if (selectedText && pdfContainerRef.current) {
      const range = selection.getRangeAt(0)
      const container = pdfContainerRef.current
      const containerRect = container.getBoundingClientRect() // 🔸 Declare early

      // Ensure selection is within the PDF container
      if (!container.contains(range.startContainer) || !container.contains(range.endContainer)) {
        return
      }

      const rangeRect = range.getBoundingClientRect()
      const isWithinContainer =
        rangeRect.left >= containerRect.left &&
        rangeRect.right <= containerRect.right &&
        rangeRect.top >= containerRect.top &&
        rangeRect.bottom >= containerRect.bottom

      if (isWithinContainer) {
        const cleanedText = selectedText
          .split("\n")
          .map((line) => line.trim())
          .join(" ")
          .replace(/\s+/g, " ")
          .trim()

        if (cleanedText.length > 0) {
          setSelectedText(cleanedText)
          setAskAIPosition({
            x: rangeRect.left - containerRect.left + rangeRect.width / 2,
            y: rangeRect.top - containerRect.top - 10,
          })
          setShowAskAIButton(true)
        }
      }
    }
  }, [])

  const handlePageChange = useCallback(
    (e) => {
      const newPage = e.currentPage + 1
      setCurrentPage(newPage)
      setStartPage(newPage)
      setEndPage(Math.min(newPage + 29, totalPages))
    },
    [totalPages],
  )

  const handlePageRangeChange = useCallback(
    (e) => {
      const { name, value } = e.target
      const newValue = Number.parseInt(value) || 1
      if (name === "startPage") {
        setStartPage(Math.min(newValue, endPage))
      } else {
        setEndPage(Math.max(newValue, startPage))
      }
    },
    [endPage, startPage],
  )

  const handleAskAI = useCallback(() => {
    if (selectedText) {
      setChatMessage(`Explain this text from the PDF: "${selectedText}"`)
      setIsChatOpen(true)
      if (isMobileView) {
        setShowPdfOnMobile(false)
      }
      setSelectedText("")
      setShowAskAIButton(false)

      // Clear the text selection
      if (window.getSelection) {
        window.getSelection().removeAllRanges()
      }
    }
  }, [selectedText, isMobileView])

  const handleAttachmentUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    setIsUploadingAttachment(true)

    const reader = new FileReader()
    reader.onload = (e) => {
      setAttachmentPreview(e.target.result)
    }
    reader.readAsDataURL(file)

    try {
      const result = await postScreenshotTeacherLibrary(
        file,
        localStorage.getItem("teacherId") || "",
        book.classRoomId,
        `attachment_${Date.now()}.${file.name.split(".").pop()}`,
      )

      const presignedUrl = await generatePresignedUrl(result.url)
      setAttachment(result.url)
      setAttachmentPresignedUrls((prev) => ({ ...prev, [result.url]: presignedUrl }))
    } catch (error) {
      console.error("Error uploading attachment:", error)
    } finally {
      setIsUploadingAttachment(false)
    }
  }

  const handleDefaultPrompt = useCallback(
    (prompt) => {
      const filledPrompt = prompt
        .replace("[start]", startPage)
        .replace("[end]", endPage)
        .replace("[topic]", "the main subject of these pages")
      setChatMessage(filledPrompt)
      setIsChatOpen(true)
      if (isMobileView) {
        setShowPdfOnMobile(false)
      }
    },
    [startPage, endPage, isMobileView],
  )

  const exitScreenshotMode = useCallback(() => {
    setIsScreenshotMode(false)
    setScreenshotOverlay(false)
    setScreenshotNotification(false)
    if (pdfContainerRef.current) {
      pdfContainerRef.current.style.cursor = "default"
    }
  }, [])

  const startScreenshotMode = useCallback(() => {
    setIsScreenshotMode((prev) => !prev)
    if (isMobileView) {
      setShowPdfOnMobile(true)
      setScreenshotNotification(true)

      setTimeout(() => {
        setScreenshotNotification(false)
      }, 3000)
    }
    setIsChatOpen(false)
    if (pdfContainerRef.current) {
      pdfContainerRef.current.style.cursor = isScreenshotMode ? "default" : "crosshair"
    }
  }, [isScreenshotMode, isMobileView])

  const handleMouseDown = useCallback(
    (e) => {
      if (!isScreenshotMode || !pdfContainerRef.current) return

      e.preventDefault()
      e.stopPropagation()

      const rect = pdfContainerRef.current.getBoundingClientRect()
      const scrollLeft = pdfContainerRef.current.scrollLeft || 0
      const scrollTop = pdfContainerRef.current.scrollTop || 0

      setIsDragging(true)
      setStartPos({
        x: e.clientX - rect.left + scrollLeft,
        y: e.clientY - rect.top + scrollTop,
      })

      setHighlightStyle({
        left: e.clientX - rect.left,
        top: e.clientY - rect.top,
        width: 0,
        height: 0,
        display: "block",
        position: "absolute",
        backgroundColor: "rgba(59, 130, 246, 0.2)",
        border: "2px solid #3b82f6",
        pointerEvents: "none",
        zIndex: 1000,
      })
    },
    [isScreenshotMode],
  )

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging || !isScreenshotMode || !pdfContainerRef.current) return

      e.preventDefault()

      const rect = pdfContainerRef.current.getBoundingClientRect()
      const scrollLeft = pdfContainerRef.current.scrollLeft || 0
      const scrollTop = pdfContainerRef.current.scrollTop || 0

      const currentPos = {
        x: e.clientX - rect.left + scrollLeft,
        y: e.clientY - rect.top + scrollTop,
      }

      const left = Math.min(startPos.x, currentPos.x)
      const top = Math.min(startPos.y, currentPos.y)
      const width = Math.abs(currentPos.x - startPos.x)
      const height = Math.abs(currentPos.y - startPos.y)

      setHighlightStyle((prev) => ({
        ...prev,
        left: left - scrollLeft,
        top: top - scrollTop,
        width,
        height,
      }))
    },
    [isDragging, isScreenshotMode, startPos],
  )

  const handleMouseUp = useCallback(async () => {
    if (!isDragging || !isScreenshotMode || !pdfContainerRef.current) return

    setIsDragging(false)
    setIsCapturingScreenshot(true)

    const rect = pdfContainerRef.current.getBoundingClientRect()
    const scrollLeft = pdfContainerRef.current.scrollLeft || 0
    const scrollTop = pdfContainerRef.current.scrollTop || 0

    const { left, top, width, height } = highlightStyle

    if (width < 10 || height < 10) {
      setHighlightStyle({ display: "none" })
      exitScreenshotMode()
      setIsCapturingScreenshot(false)
      return
    }

    try {
      const canvas = await html2canvas(pdfContainerRef.current, {
        scale: 1,
        x: left + scrollLeft,
        y: top + scrollTop,
        width: width,
        height: height,
        useCORS: true,
        allowTaint: false,
        backgroundColor: null,
        logging: false,
        removeContainer: true,
      })

      const image = canvas.toDataURL("image/png", 0.8)

      requestAnimationFrame(() => {
        setScreenshot(image)
        setScreenshotPreview(image)
        setIsChatOpen(true)
        setIsCapturingScreenshot(false)

        if (isMobileView) {
          setShowPdfOnMobile(false)
        }
      })
    } catch (error) {
      console.error("Error capturing screenshot:", error)
      setIsCapturingScreenshot(false)
    }

    setHighlightStyle({ display: "none" })
    exitScreenshotMode()
  }, [isDragging, isScreenshotMode, highlightStyle, exitScreenshotMode, isMobileView])

  const deleteScreenshot = useCallback(() => {
    setScreenshot(null)
    setScreenshotPreview(null)
  }, [])

  const openFullScreenImage = useCallback((imageUrl) => {
    if (imageUrl) {
      setFullScreenImage(imageUrl)
    }
  }, [])

  const copyToClipboard = useCallback((text, messageId) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopiedMessageId(messageId)
        setTimeout(() => setCopiedMessageId(null), 2000)
      },
      (err) => {
        console.error("Could not copy text: ", err)
      },
    )
  }, [])

  const handleSaveToNotes = async (content) => {
    setIsSaved(true)
    const notesData = {
      classRoomId: localStorage.getItem("classRoomId"),
      noteType: "chatResponse",
      noteTitle: `${book.fileTitle} chat response`,
      refTitle: book.fileTitle,
      refId: book._id,
      userId: localStorage.getItem("teacherId"),
      noteContent: JSON.stringify({
        message: content,
      }),
      mediaUrl: "",
    }

    const saveNote = await postAws("https://api-dev.aurait.ai/notes", "POST", notesData)
    if (saveNote.statusCode === 201) {
      setIsSaved(true)
    }

    setTimeout(() => {
      setIsSaved(false)
    }, 2000)
  }

  const handleBack = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.terminate()
    }
    navigate("/library")
  }, [navigate])

  // Enhanced LaTeX rendering function with better streaming support
  const renderLatex = (text) => {
    if (!text) return null

    const blockLatexRegex = /\$\$\s*([\s\S]*?)\s*\$\$/g
    const inlineLatexRegex = /\$\s*([\s\S]*?)\s*\$/g
    const bracketLatexRegex = /\\\[([\s\S]*?)\\\]/g
    const parenLatexRegex = /\\$$([\s\S]*?)\\$$/g

    let processedText = text

    processedText = processedText.replace(bracketLatexRegex, (match, content) => `$$${content}$$`)
    processedText = processedText.replace(parenLatexRegex, (match, content) => `$${content}$`)

    const blockParts = processedText.split(blockLatexRegex)

    return blockParts.map((part, blockIndex) => {
      if (blockIndex % 2 === 1) {
        try {
          return <BlockMath key={`block-${blockIndex}`} math={part.trim()} />
        } catch (error) {
          console.warn("Error rendering block math:", error)
          return (
            <span key={`block-error-${blockIndex}`} className="text-red-500">
              $$${part}$$
            </span>
          )
        }
      } else {
        const inlineParts = part.split(inlineLatexRegex)

        return inlineParts.map((inlinePart, inlineIndex) => {
          if (inlineIndex % 2 === 1) {
            try {
              return <InlineMath key={`inline-${blockIndex}-${inlineIndex}`} math={inlinePart.trim()} />
            } catch (error) {
              console.warn("Error rendering inline math:", error)
              return (
                <span key={`inline-error-${blockIndex}-${inlineIndex}`} className="text-red-500">
                  ${inlinePart}$
                </span>
              )
            }
          } else {
            return (
              <ReactMarkdown
                key={`text-${blockIndex}-${inlineIndex}`}
                components={{
                  p: ({ children }) => <p className="mb-4 leading-relaxed">{children}</p>,
                  h1: ({ children }) => <h1 className="text-2xl font-bold mb-4 text-gray-800">{children}</h1>,
                  h2: ({ children }) => <h2 className="text-xl font-bold mb-3 text-gray-800">{children}</h2>,
                  h3: ({ children }) => <h3 className="text-lg font-bold mb-2 text-gray-800">{children}</h3>,
                  h4: ({ children }) => <h4 className="text-base font-bold mb-2 text-gray-800">{children}</h4>,
                  ul: ({ children }) => <ul className="list-disc pl-6 mb-4 space-y-1">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal pl-6 mb-4 space-y-1">{children}</ol>,
                  li: ({ children }) => <li className="mb-1 leading-relaxed">{children}</li>,
                  strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
                  em: ({ children }) => <em className="italic text-gray-700">{children}</em>,
                  code: ({ children }) => (
                    <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">{children}</code>
                  ),
                  pre: ({ children }) => (
                    <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto mb-4">{children}</pre>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-700 mb-4">
                      {children}
                    </blockquote>
                  ),
                }}
              >
                {inlinePart}
              </ReactMarkdown>
            )
          }
        })
      }
    })
  }

  useEffect(() => {
    if (pdfContainerRef.current) {
      pdfContainerRef.current.style.cursor = isScreenshotMode ? "crosshair" : "default"
    }
    setScreenshotOverlay(isScreenshotMode)
  }, [isScreenshotMode])

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white">
        <div className="flex flex-col items-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
          <p className="text-gray-700 text-lg">Loading your document...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md w-full">
          <div className="flex items-center mb-4">
            <AlertCircle className="text-red-500 mr-3" size={24} />
            <p className="text-red-700 font-medium">{error}</p>
          </div>
          <button
            onClick={() => {
              setError(null)
              window.location.reload()
            }}
            className="w-full mt-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors duration-200"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-75 z-50 backdrop-blur-sm">
      <div className="absolute inset-0 flex flex-col md:flex-row">
        {(!isMobileView || (isMobileView && showPdfOnMobile)) && (
          <div
            className={`${isMobileView ? "h-[100vh] w-full" : "flex-grow"} bg-gray-100 transition-all duration-300 relative`}
            style={!isMobileView ? { marginRight: `${chatWidth}px` } : {}}
          >
            <div className="h-full flex flex-col">
              <div className="flex justify-between items-center p-2 bg-white border-b">
                <button
                  onClick={handleBack}
                  className="p-1 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors duration-200 z-10 flex items-center"
                >
                  <ArrowLeft size={20} />
                  <span className="text-sm font-medium ml-2">Back</span>
                </button>

                {isMobileView && (
                  <button
                    onClick={() => setShowPdfOnMobile(false)}
                    className="p-2 bg-blue-500 text-white rounded-md text-xs font-medium shadow-md"
                  >
                    Show Chat
                  </button>
                )}
              </div>
              <div
                className={`flex-grow overflow-hidden relative ${
                  screenshotOverlay
                    ? "before:content-[''] before:absolute before:inset-0 before:bg-black before:bg-opacity-30 before:z-10"
                    : ""
                }`}
                ref={pdfContainerRef}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
              >
                {isCapturingScreenshot && (
                  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[80]">
                    <Loader2 className="w-10 h-10 text-white animate-spin" />
                    <span className="ml-2 text-white text-lg">Capturing Screenshot...</span>
                  </div>
                )}

                {screenshotNotification && (
                  <div className="fixed top-16 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white px-4 py-2 rounded-md z-50 text-sm font-medium shadow-lg flex items-center">
                    <Camera className="mr-2 h-4 w-4" />
                    Screenshot Mode: Drag to select an area
                  </div>
                )}

                <Worker
                  workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js"
                  ref={(instance) => {
                    if (instance) {
                      setWorker(instance)
                      workerRef.current = instance
                    }
                  }}
                >
                  <div style={{ height: "100%" }}>
                    <Viewer
                      fileUrl={book.presignedUrl}
                      plugins={[defaultLayoutPluginInstance, selectionModePluginInstance]}
                      defaultScale={scale}
                      onTextLayerRender={(e) => {
                        // Enhanced text selection handling to prevent over-selection
                        if (e.textLayerRef && e.textLayerRef.addEventListener) {
                          e.textLayerRef.addEventListener("mouseup", handleTextSelection)

                          // Add additional event listeners to improve text selection accuracy
                          e.textLayerRef.addEventListener("selectstart", (event) => {
                            // Clear any existing selection state when starting a new selection
                            setShowAskAIButton(false)
                            setSelectedText("")
                          })

                          // Prevent text selection from extending beyond intended boundaries
                          e.textLayerRef.addEventListener("mousedown", (event) => {
                            // Store the initial click position for better selection control
                            const target = event.target
                            if (target && target.textContent) {
                              // Ensure we're clicking on actual text content
                              target.setAttribute("data-selection-start", "true")
                            }
                          })
                        }
                      }}
                      onPageChange={handlePageChange}
                      onDocumentLoad={(e) => {
                        setTotalPages(e.doc.numPages)
                        setPageRange({ start: 1, end: e.doc.numPages })
                      }}
                    />
                  </div>
                </Worker>
                {showAskAIButton && (
                  <button
                    onClick={handleAskAI}
                    style={{
                      position: "absolute",
                      left: `${askAIPosition.x}px`,
                      top: `${askAIPosition.y - 40}px`,
                      transform: "translateX(-50%)",
                    }}
                    className="bg-blue-500 text-white px-3 py-1 rounded-md shadow-md hover:bg-blue-600 transition-colors duration-200 z-20"
                  >
                    Ask AI
                  </button>
                )}
                <div
                  style={{
                    position: "absolute",
                    backgroundColor: "rgba(59, 130, 246, 0.2)",
                    border: "2px solid #3b82f6",
                    pointerEvents: "none",
                    zIndex: 1000,
                    ...highlightStyle,
                  }}
                ></div>
              </div>
            </div>
          </div>
        )}

        <div
          className={`${
            isMobileView
              ? `fixed inset-0 ${showPdfOnMobile ? "hidden" : "flex flex-col"} w-full h-full`
              : "fixed right-0 top-0 bottom-0 flex flex-col border-l shadow-2xl"
          } bg-white transition-all duration-300`}
          style={!isMobileView ? { width: `${chatWidth}px` } : {}}
        >
          {isMobileView && !showPdfOnMobile && (
            <button
              onClick={() => setShowPdfOnMobile(true)}
              className="absolute top-2 right-2 z-10 p-2 bg-blue-500 text-white rounded-md text-xs font-medium shadow-md"
            >
              Show PDF
            </button>
          )}

          {!isMobileView && (
            <div
              ref={chatResizeRef}
              className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize bg-gray-300 hover:bg-blue-500 transition-colors duration-200"
              onMouseDown={() => setIsResizing(true)}
            ></div>
          )}

          <div className="px-4 py-3 border-b flex items-center justify-between bg-gradient-to-r from-blue-500 to-blue-600">
            <div className="flex items-center gap-2">
              <span className="font-medium text-white text-sm">
                Study Buddy -{" "}
                {mode === "chat"
                  ? "Chat with PDF"
                  : mode === "flashcard"
                    ? "Flashcard"
                    : mode === "quiz"
                      ? "Quiz"
                      : mode === "mnemonic"
                        ? "Mnemonics"
                        : mode === "mindmap"
                          ? "Mind Map"
                          : "Flow Chart"}
              </span>
              <span
                className={`text-xs ${
                  wsStatus === "connected"
                    ? "text-green-300"
                    : wsStatus === "connecting"
                      ? "text-yellow-300"
                      : "text-red-300"
                }`}
              >
                {wsStatus === "connected" ? "Connected" : wsStatus === "connecting" ? "Connecting..." : "Disconnected"}
              </span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-2 md:p-4 bg-gray-50" ref={chatContentRef}>
            <div className="max-w-4xl mx-auto space-y-4">
              {isPdfProcessing ? (
                <div className="flex flex-col items-center justify-center p-6 bg-white rounded-lg shadow-sm border border-gray-100">
                  <div className="relative w-12 h-12 mb-3">
                    <div className="absolute inset-0 border-4 border-blue-100 rounded-full"></div>
                    <div className="absolute inset-0 border-4 border-blue-500 rounded-full animate-spin border-t-transparent"></div>
                  </div>
                  <h2 className="text-lg font-semibold text-gray-800 mb-2">Processing your PDF</h2>
                  <p className="text-gray-600 text-center text-sm">
                    Please wait while we analyze the document for you...
                  </p>
                </div>
              ) : wsStatus === "connecting" ? (
                <div className="flex flex-col items-center justify-center p-6 bg-white rounded-lg shadow-sm border border-gray-100">
                  <div className="relative w-12 h-12 mb-3">
                    <div className="absolute inset-0 border-4 border-blue-100 rounded-full"></div>
                    <div className="absolute inset-0 border-4 border-blue-500 rounded-full animate-spin border-t-transparent"></div>
                  </div>
                  <h2 className="text-lg font-semibold text-gray-800 mb-2">Loading your Study Buddy...</h2>
                </div>
              ) : chatMessages.length === 0 ? (
                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <h2 className="text-xl font-semibold text-gray-800 mb-2">
                    Welcome,{" "}
                    {JSON.parse(localStorage.getItem("teacherData") || "{}")?.teacherName?.split(" ")[0] || "teacher"}{" "}
                    👋
                  </h2>
                  <p className="text-gray-600 mb-3 text-sm">
                    I'm your AI assistant, ready to help you understand and work with this document. What would you like
                    to know?
                  </p>
                  <div className="flex gap-3 py-4 overflow-x-auto pb-2">
                    {["understand", "analyze", "generate"].map((action) => (
                      <button
                        key={action}
                        onClick={() => setSelectedAction(action)}
                        className={`flex flex-col items-center p-3 rounded-lg border transition-colors text-sm ${
                          selectedAction === action ? "bg-blue-50 text-blue-600 border-blue-200" : "hover:bg-gray-50"
                        }`}
                      >
                        <div
                          className={`h-8 w-8 rounded-full flex items-center justify-center mb-1 ${
                            selectedAction === action ? "bg-blue-100" : "bg-gray-100"
                          }`}
                        >
                          {action === "understand" && <MessageCircle className="h-4 w-4" />}
                          {action === "analyze" && <Search className="h-4 w-4" />}
                          {action === "generate" && <Sparkles className="h-4 w-4" />}
                          {action === "mindmap" && <GitBranch className="h-4 w-4" />}
                          {action === "flowchart" && <GitMerge className="h-4 w-4" />}
                        </div>
                        <span className="capitalize">{action}</span>
                      </button>
                    ))}
                  </div>
                  <div className="mt-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">✨ Try These Sample Prompts</h3>
                    <div className="flex flex-col gap-2">
                      {samplePrompts[selectedAction].map((prompt, index) => (
                        <button
                          key={index}
                          onClick={() => handleDefaultPrompt(prompt)}
                          className="flex items-center justify-start p-3 border rounded-md bg-white hover:bg-blue-50 transition-all duration-200 shadow-sm"
                        >
                          <Sparkles className="text-blue-500 mr-2 flex-shrink-0" size={16} />
                          <span className="text-sm text-gray-800">{prompt}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {chatMessages.map((message, index) => (
                    <div
                      key={index}
                      className={`flex ${message.role === "user" ? "justify-end" : "justify-start"} w-full`}
                    >
                      <div
                        className={`${
                          message.role === "user"
                            ? "bg-blue-500 text-white rounded-br-none"
                            : "bg-gray-100 text-gray-800 rounded-bl-none"
                        } ${message.attachment ? "w-full" : "max-w-[85%] md:max-w-[70%]"} rounded-lg overflow-hidden`}
                      >
                        {message.attachment && (
                          <div className="w-full p-2">
                            <button
                              onClick={() =>
                                openFullScreenImage(
                                  attachmentPresignedUrls[message.attachment] ||
                                    screenshotPresignedUrls[message.attachment],
                                )
                              }
                              className="flex items-center space-x-2 text-white hover:text-amber-300 transition-colors duration-200"
                            >
                              <ImageIcon size={18} />
                              <span>View attached image</span>
                            </button>
                          </div>
                        )}

                        {/* FIXED: Only show content for chat messages or completed non-chat messages */}
                        {message.content && !message.showThinkingDots && (
                          <div className="p-3 markdown-content text-sm relative space-y-2">
                            <div className="pr-20">{renderLatex(message.content)}</div>
                            {message.role === "assistant" && (
                              <div className="absolute top-2 right-2 flex gap-1">
                                <button
                                  onClick={() => handleAddToNotes("text", message.content)}
                                  className="p-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-full transition-all duration-300 ease-in-out shadow-lg"
                                  title="Add to Notes"
                                >
                                  <Plus size={12} />
                                </button>
                                <button
                                  onClick={() => copyToClipboard(message.content, index)}
                                  className="p-1.5 rounded-full hover:bg-gray-300 transition-colors duration-200"
                                  title="Copy response"
                                >
                                  {copiedMessageId === index ? (
                                    <Check size={12} className="text-green-500" />
                                  ) : (
                                    <Copy size={12} />
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* FIXED: Show thinking dots for non-chat streaming messages */}
                        {message.isStreaming && message.showThinkingDots && (
                          <div className="p-3">
                            <div className="flex items-center space-x-2">
                              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                              <div
                                className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                                style={{ animationDelay: "0.2s" }}
                              ></div>
                              <div
                                className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                                style={{ animationDelay: "0.4s" }}
                              ></div>
                            </div>
                          </div>
                        )}

                        {message.role === "assistant" && message.flashcard ? (
                          <div className="relative">
                            <button
                              onClick={() => handleAddToNotes("flashcard", message.flashcard)}
                              className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-full transition-all duration-300 ease-in-out shadow-lg absolute z-10 top-6 right-3"
                              title="Add to Notes"
                            >
                              <Plus size={14} />
                            </button>
                            <Flashcard front={message.flashcard.front} back={message.flashcard.back} book={book} />
                          </div>
                        ) : null}
                        {message.role === "assistant" && message.questionArray ? (
                          <QuizQuestion quizData={message.questionArray} />
                        ) : null}
                        {message.role === "assistant" && message.mnemonicDevice ? (
                          <div className="relative">
                            <button
                              onClick={() => handleAddToNotes("mnemonic", message.mnemonicDevice)}
                              className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-full transition-all duration-300 ease-in-out shadow-lg absolute z-10 top-6 right-3"
                              title="Add to Notes"
                            >
                              <Plus size={14} />
                            </button>
                            <MnemonicDevice message={message} book={book} />
                          </div>
                        ) : null}
                        {message.role === "assistant" && message.flowchart ? (
                          <div className="relative">
                            <button
                              onClick={() => handleAddToNotes("flowchart", message.flowchart)}
                              className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-full transition-all duration-300 ease-in-out shadow-lg absolute z-10 top-6 right-3"
                              title="Add to Notes"
                            >
                              <Plus size={14} />
                            </button>
                            <FlowChart message={message} book={book} />
                          </div>
                        ) : null}
                        {message.role === "assistant" && message.mindmap ? (
                          <div className="relative">
                            <button
                              onClick={() => handleAddToNotes("mindmap", message.mindmap)}
                              className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-full transition-all duration-300 ease-in-out shadow-lg absolute z-10 top-6 right-3"
                              title="Add to Notes"
                            >
                              <Plus size={14} />
                            </button>
                            <MindMap message={message} book={book} />
                          </div>
                        ) : null}
                        {message.role === "assistant" &&
                          message.followUpQuestions &&
                          message.followUpQuestions.length > 0 && (
                            <div className="mt-3 pt-3 border-t">
                              <div className="bg-blue-50 rounded-lg shadow-sm overflow-hidden border border-blue-100">
                                <div className="bg-blue-100 p-3 flex items-center justify-between">
                                  <div className="flex items-center space-x-2">
                                    <Sparkles className="text-blue-500" size={16} />
                                    <span className="font-medium text-blue-700 text-sm">
                                      Suggested follow-up questions
                                    </span>
                                  </div>
                                </div>
                                <div className="divide-y divide-blue-200">
                                  {message.followUpQuestions.map((question, idx) => (
                                    <button
                                      key={idx}
                                      onClick={() => handleDefaultPrompt(question)}
                                      className="w-full p-3 flex items-center justify-between hover:bg-blue-100 transition-colors duration-200 group text-left"
                                    >
                                      <span className="text-sm text-blue-800 group-hover:text-blue-900 flex-1 pr-2">
                                        {question}
                                      </span>
                                      <ArrowRight
                                        className="text-blue-400 group-hover:text-blue-500 transform group-hover:-translate-x-1 transition-all duration-200 flex-shrink-0"
                                        size={16}
                                      />
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  ))}
                  {/* FIXED: Only show general waiting indicator for chat mode */}
                  {isWaitingForResponse && mode === "chat" && (
                    <div className="flex justify-start w-full">
                      <div className="bg-gray-100 text-gray-800 rounded-lg overflow-hidden max-w-[70%] p-3">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                          <div
                            className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                            style={{ animationDelay: "0.2s" }}
                          ></div>
                          <div
                            className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                            style={{ animationDelay: "0.4s" }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  )}
                  {chatError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center">
                        <AlertCircle className="text-red-500 mr-2" size={20} />
                        <p className="text-red-700 text-sm">{chatError}</p>
                      </div>
                      <button
                        onClick={() => connectWebSocket()}
                        className="mt-2 text-sm text-red-600 hover:text-red-700 font-medium bg-white border border-red-300 px-4 py-2 rounded-md"
                      >
                        Refresh
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="p-3 border-t bg-white">
            <div className="flex items-center mb-2 space-x-1 overflow-x-auto whitespace-nowrap pb-2 px-1">
              <button
                onClick={startScreenshotMode}
                className={`p-1 rounded-full hover:bg-blue-50 flex items-center ${
                  isScreenshotMode ? "text-blue-600 bg-blue-100" : "text-blue-500"
                }`}
              >
                <Crop size={isMobileView ? 12 : 14} />
                <span className="ml-1 text-xs hidden sm:inline">Screenshot</span>
              </button>
              <button
                onClick={() => {
                  setMode("chat")
                }}
                className={`p-1 rounded-full hover:bg-blue-50 flex items-center ${
                  mode === "chat" ? "text-blue-600 bg-blue-100" : "text-blue-500"
                }`}
              >
                <MessageCircle size={isMobileView ? 12 : 14} />
                <span className="ml-1 text-xs hidden sm:inline">Chat</span>
              </button>
              <button
                onClick={() => {
                  setMode("flashcard")
                  setChatMessage("Create flashcard for")
                }}
                className={`p-1 rounded-full hover:bg-blue-50 flex items-center ${
                  mode === "flashcard" ? "text-blue-600 bg-blue-100" : "text-blue-500"
                }`}
              >
                <BookOpen size={isMobileView ? 12 : 14} />
                <span className="ml-1 text-xs hidden sm:inline">Flashcard</span>
              </button>
              <button
                onClick={() => {
                  setMode("quiz")
                  setChatMessage("Create me a quiz for")
                }}
                className={`p-1 rounded-full hover:bg-blue-50 flex items-center ${
                  mode === "quiz" ? "text-blue-600 bg-blue-100" : "text-blue-500"
                }`}
              >
                <HelpCircle size={isMobileView ? 12 : 14} />
                <span className="ml-1 text-xs hidden sm:inline">Quiz</span>
              </button>
              <button
                onClick={() => {
                  setMode("mnemonic")
                  setChatMessage("Help me in creating mnemonic device for")
                }}
                className={`p-1 rounded-full hover:bg-blue-50 flex items-center ${
                  mode === "mnemonic" ? "text-blue-600 bg-blue-100" : "text-blue-500"
                }`}
              >
                <Brain size={isMobileView ? 12 : 14} />
                <span className="ml-1 text-xs hidden sm:inline">Mnemonic</span>
              </button>
              <button
                onClick={() => {
                  setMode("mindmap")
                  setChatMessage("Create a mind map for")
                }}
                className={`p-1 rounded-full hover:bg-blue-50 flex items-center ${
                  mode === "mindmap" ? "text-blue-600 bg-blue-100" : "text-blue-500"
                }`}
              >
                <GitBranch size={isMobileView ? 12 : 14} />
                <span className="ml-1 text-xs hidden sm:inline">Mind Map</span>
              </button>
              <button
                onClick={() => {
                  setMode("flowchart")
                  setChatMessage("Create a flow chart for")
                }}
                className={`p-1 rounded-full hover:bg-blue-50 flex items-center ${
                  mode === "flowchart" ? "text-blue-600 bg-blue-100" : "text-blue-500"
                }`}
              >
                <GitMerge size={isMobileView ? 12 : 14} />
                <span className="ml-1 text-xs hidden sm:inline">Flow Chart</span>
              </button>
            </div>

            {screenshotPreview && (
              <div className="mb-2 relative">
                <img
                  src={screenshotPreview || "/placeholder.svg"}
                  alt="Screenshot Preview"
                  className="rounded-md max-h-40 object-contain"
                />
                {isScreenshotLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded">
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                    <span className="ml-2 text-white text-sm">Processing Screenshot...</span>
                  </div>
                )}
                <button
                  onClick={deleteScreenshot}
                  className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center transition-colors duration-200"
                >
                  <X size={14} />
                </button>
              </div>
            )}

            {attachmentPreview && (
              <div className="mb-2 relative">
                <img
                  src={attachmentPreview || "/placeholder.svg"}
                  alt="Attachment Preview"
                  className="rounded-md max-h-40 object-contain"
                />
                <button
                  onClick={() => {
                    setAttachment(null)
                    setAttachmentPreview(null)
                  }}
                  className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center transition-colors duration-200"
                >
                  <X size={14} />
                </button>
              </div>
            )}

            <form onSubmit={handleSendMessage} className="relative flex items-center space-x-2 p-2">
              <div className="relative">
                <input
                  type="file"
                  id="attachment-upload"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAttachmentUpload}
                />
                <div className="relative group">
                  <button
                    type="button"
                    onClick={() => document.getElementById("attachment-upload").click()}
                    className="p-2 text-gray-500 hover:text-blue-500 transition-colors duration-200 rounded-full hover:bg-gray-100 flex items-center"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                    Attach an image
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                  </div>
                </div>
              </div>

              <div className="relative group">
                <button
                  type="button"
                  onClick={async () => {
                    if (isTutorActive) {
                      stopSession()
                      setIsTutorActive(false)
                    } else {
                      const success = await startSession()
                      if (success) {
                        setIsTutorActive(true)
                      }
                    }
                  }}
                  className="p-2 text-gray-500 hover:text-blue-500 transition-colors duration-200 rounded-full hover:bg-gray-100 flex items-center"
                >
                  {isTutorActive ? (
                    <div className="flex items-center space-x-0.5">
                      <div className="w-1 h-3 bg-blue-500 rounded-full animate-sound-wave"></div>
                      <div
                        className="w-1 h-5 bg-blue-500 rounded-full animate-sound-wave"
                        style={{ animationDelay: "0.2s" }}
                      ></div>
                      <div
                        className="w-1 h-4 bg-blue-500 rounded-full animate-sound-wave"
                        style={{ animationDelay: "0.1s" }}
                      ></div>
                    </div>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6 21V19C6 17.9391 6.42143 16.9217 7.17157 16.1716C7.92172 15.4214 8.93913 15 10 15H14C15.0609 15 16.0783 15.4214 16.8284 16.1716C17.5786 16.9217 18 17.9391 18 19V21"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                  {isTutorActive ? "Disable human tutor" : "Enable human tutor"}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                </div>
              </div>

              <textarea
                value={chatMessage}
                onChange={(e) => {
                  setChatMessage(e.target.value)
                  e.target.style.height = "auto"
                  e.target.style.height = `${Math.min(e.target.scrollHeight, 150)}px`
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault()
                    handleSendMessage(e)
                  } else if (e.key === "Enter" && e.shiftKey) {
                    e.preventDefault()
                    setChatMessage((prev) => prev + "\n")
                  }
                }}
                placeholder="Type a message..."
                className="flex-grow p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 text-sm resize-none overflow-hidden"
                style={{
                  minHeight: isMobileView ? "40px" : "50px",
                  maxHeight: isMobileView ? "100px" : "150px",
                  paddingRight: "40px",
                }}
                rows={isMobileView ? 1 : 2}
              />

              <button
                type="submit"
                className="absolute top-1/2 right-3 transform -translate-y-1/2 bg-blue-500 hover:bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors duration-200"
              >
                <ArrowLeft className="transform rotate-180" size={16} />
              </button>
            </form>
          </div>
        </div>
      </div>
      {fullScreenImage && (
        <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
          <img
            src={fullScreenImage || "/placeholder.svg"}
            alt="Full Screen"
            className="max-w-full max-h-full object-contain"
          />
          <button
            onClick={() => setFullScreenImage(null)}
            className="absolute top-4 right-4 bg-gray-800 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-gray-700 transition-colors duration-200"
          >
            <X size={20} />
          </button>
        </div>
      )}

      {/* Enhanced Add to Notes Modal with improved note naming and folder handling */}
      {showAddToNotesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Add to Notes</h3>

            <div className="flex space-x-4 mb-4">
              <button
                onClick={() => {
                  setIsCreatingNewNote(true)
                  setSelectedFolder("")
                  setCustomNoteName("")
                }}
                className={`px-4 py-2 rounded-md ${
                  isCreatingNewNote ? "bg-blue-500 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Create New
              </button>
              <button
                onClick={() => {
                  setIsCreatingNewNote(false)
                  setNewNoteName("")
                }}
                className={`px-4 py-2 rounded-md ${
                  !isCreatingNewNote ? "bg-blue-500 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Add to Existing
              </button>
            </div>

            {isCreatingNewNote ? (
              <div className="mb-4">
                <label htmlFor="new-note-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Note Name
                </label>
                <input
                  type="text"
                  id="new-note-name"
                  value={newNoteName}
                  onChange={(e) => setNewNoteName(e.target.value)}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter a name for your note"
                />
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <label htmlFor="folder-select" className="block text-sm font-medium text-gray-700 mb-1">
                    Select Folder
                  </label>
                  {isLoadingFolders ? (
                    <div className="flex items-center justify-center p-4">
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      <span className="text-sm text-gray-600">Loading folders...</span>
                    </div>
                  ) : (
                    <select
                      id="folder-select"
                      value={selectedFolder}
                      onChange={(e) => setSelectedFolder(e.target.value)}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select a folder</option>
                      {noteFolders.map((folder, index) => (
                        <option key={index} value={typeof folder === "string" ? folder : folder.folderName}>
                          {typeof folder === "string"
                            ? folder
                            : `${folder.folderName} (${folder.noteCount || 0} notes)`}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Enhanced note naming section for existing folders */}
                <div className="mb-4">
                  <label htmlFor="custom-note-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Note Name (Optional)
                  </label>
                  <input
                    type="text"
                    id="custom-note-name"
                    value={customNoteName}
                    onChange={(e) => setCustomNoteName(e.target.value)}
                    className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={`Leave empty for default: ${generateDefaultNoteName(selectedMessageContent)}`}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    If left empty, a default name will be generated based on the content type and timestamp.
                  </p>
                </div>
              </>
            )}

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => {
                  setShowAddToNotesModal(false)
                  setSelectedFolder("")
                  setNewNoteName("")
                  setCustomNoteName("")
                }}
                className="px-4 py-2 border rounded-md hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveToNotesModal}
                disabled={isLoadingFolders}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default StudyBuddy
